import { ModuleProperties } from '@/lib/core/types';
import { ApiBlockListModel, MembershipDto } from '@/services/platform/api/model';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useMemberCardStore } from '@/lib/core/stores/useMemberCardStore';
import { useApp } from '@/lib/core/providers/appProvider';
import { Icon } from '@/components/ui/icon';
import { QrCodeIcon } from '@/components/shared/icons/qr-code-icon';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { NotificationBell } from '@/components/shared/icons/notification-bell';
import { useEffect, useState } from 'react';
import { getMembership } from '@/services/platform/data/profile';
import { cn } from '@/lib/core';

interface MemberCardProperties {
  title?: string;
  titleHeading?: string;
  icon?: string;
  profileMode?: boolean;
  scanLabel?: string;
  memberTypeBackgrounds?: ApiBlockListModel;
}

export const YourMemberCard = (props: ModuleProperties) => {
  const properties = props.content?.properties as MemberCardProperties | undefined;
  const { openModal } = useUIStore();
  const { setCmsProperties } = useMemberCardStore();
  const { appSettings, memberTheme } = useApp();

  const [membership, setMembership] = useState<MembershipDto | undefined>(undefined);

  useEffect(() => {
    getMembership().then(setMembership);
  }, []);

  const hasCard = membership?.hasCard ?? membership?.cardId != null;
  const isProfileMode = properties?.profileMode === true;

  const label = properties?.titleHeading ?? properties?.title ?? 'YOUR MEMBER CARD';

  const handleOpen = () => {
    if (isProfileMode) {
      setCmsProperties({
        titleHeading: label,
        scanLabel: appSettings?.properties?.memberCardScanLabel,
        memberTypeBackgrounds: appSettings?.properties?.memberTypeBackgrounds,
      });
    } else {
      setCmsProperties({
        titleHeading: properties?.titleHeading,
        icon: properties?.icon,
        scanLabel: properties?.scanLabel,
        memberTypeBackgrounds:
          properties?.memberTypeBackgrounds ?? appSettings?.properties?.memberTypeBackgrounds,
      });
    }
    openModal('member-card');
  };

  // ── Profile mode ─────────────────────────────────────────────────────
  if (isProfileMode) {
    if (!hasCard) return null;

    return (
      <ModuleWrapper
        component={props}
        isFullWidth={true}
        classNames={{
          base: 'px-4',
          wrapper: 'py-0 lg:py-0',
          grid: 'grid-cols-1 lg:grid-cols-1 gap-0',
        }}
      >
        <button
          className="flex w-full flex-col items-center justify-center gap-2 rounded-[2px] px-2 py-4 drop-shadow-[0px_8px_24px_rgba(0,0,0,0.24)]"
          style={{ background: memberTheme.cardBg, border: `1px solid ${memberTheme.cardAccent}` }}
          aria-label={label}
          onClick={handleOpen}
        >
          {properties?.icon ? (
            <Icon name={properties.icon} width={32} height={32} style={{ color: memberTheme.cardAccent }} />
          ) : (
            <QrCodeIcon width={32} height={32} style={{ color: memberTheme.cardAccent }} />
          )}
          <span
            className="font-inter text-[12px] font-medium tracking-[0.5px] whitespace-nowrap uppercase"
            style={{ color: memberTheme.cardAccent }}
          >
            {label}
          </span>
        </button>
      </ModuleWrapper>
    );
  }

  // ── Homepage mode ─────────────────────────────────────────────────────
  return (
    <ModuleWrapper
      component={props}
      isFullWidth={true}
      classNames={{
        base: 'module__navigation-bar pointer-events-none absolute top-0 right-0 left-0 z-50 my-0!',
        wrapper: 'py-0 lg:py-0',
        grid: 'grid-cols-1 lg:grid-cols-1 gap-0',
      }}
    >
      <div className="relative w-full">
        <div
          className={cn(
            'pt-safe-area fixed top-0 z-80 flex w-full',
            'bg-linear-to-b from-black/80 to-transparent pb-4 backdrop-blur-[2.5px]'
          )}
        >
          <div className="pointer-events-auto col-span-1 flex w-full items-center justify-between px-2">
            {hasCard ? (
              <button
                className="flex items-center gap-1 rounded-[52px] px-2 py-2"
                aria-label={label}
                onClick={handleOpen}
              >
                <span className="font-pp-neue-montreal text-[12px] font-semibold tracking-[0.5px] text-white uppercase drop-shadow-md">
                  {label}
                </span>
                <span className="flex size-6 items-center justify-center overflow-hidden" aria-hidden="true">
                  {properties?.icon ? (
                    <Icon name={properties.icon} width={24} height={24} color="white" />
                  ) : (
                    <QrCodeIcon width={24} height={24} className="text-white" />
                  )}
                </span>
              </button>
            ) : (
              <div />
            )}

            <NotificationBell />
          </div>
        </div>
      </div>
    </ModuleWrapper>
  );
};
